import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Button,
  Space,
  message,
  Spin,
  Typography,
  Row,
  Col,
  Alert,
  Tag,
  Input,
  Progress,
  Collapse,
  Checkbox
} from 'antd';
import {
  TranslationOutlined,
  CopyOutlined,
  ClearOutlined,
  FileTextOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  SoundOutlined
} from '@ant-design/icons';
import { axiosPost } from '../../services/http.services';
import {API_FETCH_AUDIO_FILES, API_TRANSLATE} from '../../api/api';

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;



const SUPPORTED_LANGUAGES = [
  { code: "hi", name: "Hindi", nativeName: "हिंदी", serviceId: "ai4bharat/indictrans-v2-all-gpu--t4" },
  { code: "ta", name: "Tamil", nativeName: "தமிழ்", serviceId: "bhashini/bodhan/Indic-trans-v4" },
  { code: "bn", name: "Bengali", nativeName: "বাংলা", serviceId: "ai4bharat/indictrans-v2-all-gpu--t4" },
  { code: "mr", name: "Marathi", nativeName: "मराठी", serviceId: "bhashini/bodhan/Indic-trans-v4" },
  { code: "kn", name: "Kannada", nativeName: "ಕನ್ನಡ", serviceId: "bhashini/bodhan/Indic-trans-v4" },
  { code: "te", name: "Telugu", nativeName: "తెలుగు", serviceId: "ai4bharat/indictrans-v2-all-gpu--t4" },
  { code: "as", name: "Assamese", nativeName: "অসমীয়া", serviceId: "ai4bharat/indictrans-v2-all-gpu--t4" },
  { code: "gu", name: "Gujarati", nativeName: "ગુજરાતી", serviceId: "ai4bharat/indictrans-v2-all-gpu--t4" },
  { code: "ml", name: "Malayalam", nativeName: "മലയാളം", serviceId: "bhashini/bodhan/Indic-trans-v4" },
  { code: "or", name: "Odia", nativeName: "ଓଡିଆ", serviceId: "ai4bharat/indictrans-v2-all-gpu--t4" },
  { code: "pa", name: "Punjabi", nativeName: "ਪੰਜਾਬੀ", serviceId: "bhashini/bodhan/Indic-trans-v4" },
  { code: "brx", name: "Bodo", nativeName: "Bodo", serviceId: "bhashini/iiith/nmt-all" },
  { code: "sd", name: "Sindhi", nativeName: "Sindhi", serviceId: "ai4bharat/indictrans-v2-all-gpu--t4" },
  { code: "ne", name: "Nepali", nativeName: "नेपाली", serviceId: "ai4bharat/indictrans-v2-all-gpu--t4" },
  { code: "sa", name: "Sanskrit", nativeName: "संस्कृतम्", serviceId: "ai4bharat/indictrans-v2-all-gpu--t4" },
  { code: "ur", name: "Urdu", nativeName: "اردو", serviceId: "bhashini/iiith/nmt-all" },
  { code: "mni", name: "Manipuri", nativeName: "Manipuri", serviceId: "bhashini/bodhan/Indic-trans-v4" },
  { code: "doi", name: "Dogri", nativeName: "Dogri", serviceId: "ai4bharat/indictrans-v2-all-gpu--t4" },
  { code: "sat", name: "Santali", nativeName: "Santali", serviceId: "bhashini/iiith/nmt-all" },
  { code: "mai", name: "Maithili", nativeName: "मैथिली", serviceId: "bhashini/iiith/nmt-all" },
  { code: "gom", name: "Konkani", nativeName: "कोंकणी", serviceId: "bhashini/iiith/nmt-all" },
  { code: "ks", name: "Kashmiri", nativeName: "कश्मीरी", serviceId: "bhashini/iiith/nmt-all" }
];

const CHUNK_SIZE_OPTIONS = [10, 15, 20, 30, 50];

// U+001F (Unit Separator) as path segment delimiter — never appears in i18n keys.
// U+001E (Record Separator) as array-index prefix — distinguishes array indices
// from object keys that happen to look numeric (e.g. { "0": "val" } vs ["val"]).
const _PATH_SEP = '\u001F';
const _ARR_PFX  = '\u001E';

const flattenObject = (obj, prefix = '') => {
  const isArray = Array.isArray(obj);
  const entries = isArray
    ? obj.map((v, i) => [`${_ARR_PFX}${i}`, v])
    : Object.entries(obj);
  return entries.reduce((acc, [key, value]) => {
    const fullKey = prefix ? `${prefix}${_PATH_SEP}${key}` : key;
    if (typeof value === 'object' && value !== null) {
      Object.assign(acc, flattenObject(value, fullKey));
    } else {
      acc[fullKey] = value;
    }
    return acc;
  }, {});
};

const unflattenObject = (flat) => {
  const keys = Object.keys(flat);
  if (keys.length === 0) return {};
  const rootIsArray = keys[0].split(_PATH_SEP)[0].startsWith(_ARR_PFX);
  const root = rootIsArray ? [] : {};
  for (const [path, value] of Object.entries(flat)) {
    const segments = path.split(_PATH_SEP);
    let current = root;
    for (let i = 0; i < segments.length - 1; i++) {
      const seg = segments[i];
      const nextSeg = segments[i + 1];
      const key = seg.startsWith(_ARR_PFX) ? Number(seg.slice(1)) : seg;
      if (current[key] == null) {
        current[key] = nextSeg.startsWith(_ARR_PFX) ? [] : {};
      }
      current = current[key];
    }
    const lastSeg = segments[segments.length - 1];
    current[lastSeg.startsWith(_ARR_PFX) ? Number(lastSeg.slice(1)) : lastSeg] = value;
  }
  return root;
};

const ALL_AUDIO_LANGUAGES = [
  { code: "en", name: "English", nativeName: "English" },
  ...SUPPORTED_LANGUAGES
];

// sourceName must match the value the consent screen injects into {{UIDAI}}.
const VC_CONSENT_SOURCES = [
  { id: 'uidai', label: 'UIDAI', sourceName: 'UIDAI' },
  { id: 'digilocker', label: 'DigiLocker', sourceName: 'Digi Locker' },
  { id: 'passport', label: 'Passport', sourceName: 'Passport' },
];

const CONSENT_AUDIO_OPTIONS = [
  ...VC_CONSENT_SOURCES.map(({ id, label }) => ({ key: `wbta_vc_header_${id}`, label: `VC Header (${label})` })),
  ...VC_CONSENT_SOURCES.map(({ id, label }) => ({ key: `wbta_vc_minor_header_${id}`, label: `VC Minor Header (${label})` })),
  { key: 'wbta_vc_body', label: 'VC Body (Common)' },
  { key: 'wbta_vc_minor_body', label: 'VC Minor Body (Common, after guardian name)' },
  { key: 'wbta_bp_header', label: 'Boarding Pass Header' },
  { key: 'wbta_hs_bp_header', label: 'Hub & Spoke BP Header' },
  { key: 'wbta_bp_body', label: 'Boarding Pass Body (Common)' },
  { key: 'wbta_hs_bp_body', label: 'Hub & Spoke BP Body' },
];

// Mirrors the "What is being shared?" tags rendered by ConsentScreen.
const VC_SHARED_TAGS = [
  'NAMEE',
  'consent.dataLabels.dob',
  'GENDER',
  'EXPIRY_DATE_LABEL',
  'FACIAL_IMAGE',
  'IDENTITY_DOC_NUMBER',
];

// Mirrors the BP data chips rendered by ConsentScreen.
const BP_SHARED_SECTIONS = [
  {
    labelKey: 'consent.dataLabels.travelDetails',
    tags: [
      'NAMEE',
      'FLIGHT',
      { literal: 'PNR' },
      'consent.dataLabels.sequenceNumber',
      { literal: 'Seat' },
      'consent.dataLabels.travelDate',
      'consent.dataLabels.origin',
      'consent.dataLabels.destination',
    ],
  },
  {
    labelKey: 'consent.dataLabels.credentialDetails',
    hubSpokeLabelKey: 'consent.dataLabels.digiYatraVc',
    tags: [
      'consent.dataLabels.selfieImage',
      'consent.dataLabels.dateOfBirth',
      'consent.dataLabels.nameAsPerId',
      'consent.dataLabels.maskedId',
    ],
  },
];

// Chrome silently drops programmatic downloads beyond ~10 per burst, so downloads are serialized with a gap.
const DOWNLOAD_GAP_MS = 400;
let downloadQueue = Promise.resolve();

const queueBlobDownload = (blob, fileName) => {
  downloadQueue = downloadQueue.then(() => new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      URL.revokeObjectURL(url);
      resolve();
    }, DOWNLOAD_GAP_MS);
  }));
  return downloadQueue;
};

const BhashiniService = () => {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [language, setLanguage] = useState("hi");
  const [excludedKeys, setExcludedKeys] = useState("DISCLAIMER_TEXT, bhashini, BP_SHARING_FOOTER_DESC, WALLET_FOOTER_DESC, MENU_FOOTER_DESC, PREVIOUS_TRIPS_FOOTER_DESC");
  const [chunkSize, setChunkSize] = useState(50);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [translationStats, setTranslationStats] = useState(null);
  const [translationHistory, setTranslationHistory] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [selectedAudioKeys, setSelectedAudioKeys] = useState(CONSENT_AUDIO_OPTIONS.map(o => o.key));
  const [selectedLanguages, setSelectedLanguages] = useState(ALL_AUDIO_LANGUAGES.map(l => l.code));
  const [languageErrors, setLanguageErrors] = useState([]);
  const [skipConsentValidation, setSkipConsentValidation] = useState(false);
  const [audioProgressLog, setAudioProgressLog] = useState([]);

  const saveToHistory = (translation) => {
    try {
      const newHistory = [
        {
          id: Date.now(),
          timestamp: new Date().toISOString(),
          sourceLanguage: "en",
          targetLanguage: language,
          inputText: inputText.substring(0, 100) + (inputText.length > 100 ? '...' : ''),
          success: true,
          keyCount: Object.keys(JSON.parse(inputText)).length
        },
        ...translationHistory.slice(0, 9)
      ];

      setTranslationHistory(newHistory);
    } catch (error) {
      console.error('Error saving to history:', error);
    }
  };

  const createChunks = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const extractTrailingDots = (text) => {
    const match = text.match(/^(.*?)(\.{2,})$/);
    if (match) {
      return {
        content: match[1],
        dots: match[2]
      };
    }
    return { content: text, dots: '' };
  };

  const extractPlaceholders = (text) => {
    const placeholders = [];
    let modifiedText = text;
  
    const placeholderPattern = /\{\{([^}]+)\}\}/g;
    const matches = [...text.matchAll(placeholderPattern)];
  
    matches.forEach((match, index) => {
      const fullPlaceholder = match[0];
      const innerContent = match[1].trim();
  
      placeholders.push({
        index: index,
        original: fullPlaceholder,
        innerContent: innerContent
      });
    });
  
    return { modifiedText, placeholders };
  };
  
  const restorePlaceholders = (translatedText, placeholders) => {
    let restoredText = translatedText;
  
    placeholders.slice().reverse().forEach(({ original, innerContent }) => {
      const regex = /(.*?)({{[^{}]*?}}?)(.*)/;
      const match = restoredText.match(regex);

      if (match) {
        const part1 = match[1].trim(); 
        const part3 = match[3].trim();
      
        const finalString = `${part1} ${original} ${part3}`.trim().replace(/\s+/g, ' ');
       
        restoredText = finalString;
      }
    });

    if(restoredText.includes('customlink')){
      restoredText = restoredText.replace(/customlink/gi, 'CustomLink');
      restoredText = restoredText
      .replace(/<CustomLink>/g, ' <CustomLink>')
      .replace(/<\/CustomLink>/g, '</CustomLink> ');
    }
  
    return restoredText;
  };

  const downloadAudioFile = (base64Audio, fileName, mimeType) => {
    const binary = atob(base64Audio);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: mimeType });
    return queueBlobDownload(blob, fileName);
  };

  const joinSegments = (segments) => {
    return segments
      .map((segment) => (segment || '').trim())
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const replaceTemplateToken = (text, token, replacement) => {
    const tokenPattern = new RegExp(`\\{\\{\\s*${token}\\s*\\}\\}`, 'gi');
    return (text || '').replace(tokenPattern, replacement);
  };

  const stripAudioMarkup = (text) => {
    return (text || '')
      .replace(/<\/?CustomLink>/gi, ' ')
      .replace(/<\/?StrongAirport>/gi, ' ')
      .replace(/<\/?DigiYatraLink>/gi, ' ')
      .replace(/<\/?AirportLink>/gi, ' ')
      .replace(/<\/?Strong>/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Supports both nested ("consent.title") and top-level keys.
  const getValueByPath = (source, path) => {
    if (!source || typeof source !== 'object') return undefined;
    if (typeof source[path] === 'string') return source[path];
    return path.split('.').reduce(
      (acc, segment) => (acc && typeof acc === 'object' ? acc[segment] : undefined),
      source
    );
  };

  const createConsentTextReader = (valueByKey) => {
    const missingKeys = new Set();
    const read = (key) => {
      const value = getValueByPath(valueByKey, key);
      if (typeof value === 'string' && value.trim()) return value.trim();
      missingKeys.add(key);
      return '';
    };
    return { read, missingKeys };
  };

  // Terminating punctuation gives the TTS engine a natural pause between sections.
  const endSentence = (text) => {
    const cleaned = stripAudioMarkup(text);
    if (!cleaned) return '';
    return /[.?!:;।॥]$/.test(cleaned) ? cleaned : `${cleaned}.`;
  };

  const joinSentences = (parts) => joinSegments(parts.map(endSentence));

  const readTagList = (read, tags) => tags
    .map((tag) => (typeof tag === 'string' ? read(tag) : tag.literal))
    .map((label) => label.replace(/\s*#\s*$/, '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(', ');

  // Splits around {{guardianName}} so the app can splice the spoken name in between.
  const splitAtGuardianName = (text) => {
    const match = text.match(/\{\{\s*guardianName\s*\}\}/i);
    if (!match) return { before: text, after: '' };
    return {
      before: text.slice(0, match.index).trim(),
      after: text.slice(match.index + match[0].length).replace(/^[\s,،]+/, '').trim()
    };
  };

  const buildVcBody = (read, durationKey) => joinSentences([
    joinSentences([read('consent.dataHeader'), read('DIGIYATRA_FOUNDATION')]),
    joinSentences([read('consent.shareHeader'), readTagList(read, VC_SHARED_TAGS)]),
    joinSentences([read('consent.purposeHeader'), read('PURPOSE_AADHAAR')]),
    joinSentences([read('consent.durationHeader'), read(durationKey)]),
    joinSentences([read('IMPORTANT'), read('NO_DATA_AADHAAR')]),
    read('I_AGREE_AND_CONFIRM_THE_PRIVACY_POLICY2')
  ]);

  const buildBpSharedData = (read, isHubSpoke = false) => joinSentences([
    read('consent.shareHeader'),
    ...BP_SHARED_SECTIONS.map(({ labelKey, hubSpokeLabelKey, tags }) => {
      const sectionLabelKey = isHubSpoke && hubSpokeLabelKey ? hubSpokeLabelKey : labelKey;
      return `${read(sectionLabelKey)}: ${readTagList(read, tags)}`;
    })
  ]);

  const CONSENT_TEXT_BUILDERS = {
    ...VC_CONSENT_SOURCES.reduce((acc, { id, sourceName }) => {
      acc[`wbta_vc_header_${id}`] = (read) => joinSentences([
        read('consent.title'),
        replaceTemplateToken(read('CONSENT_WITHOUT_GUARDIAN'), 'UIDAI', sourceName)
      ]);
      acc[`wbta_vc_minor_header_${id}`] = (read) => endSentence(read('consent.title'));
      return acc;
    }, {}),
    wbta_vc_body: (read) => buildVcBody(read, 'DURATION_AADHAAR'),
    wbta_vc_minor_body: (read) => joinSentences([
      splitAtGuardianName(read('CONSENT_WITH_GUARDIAN')).after,
      buildVcBody(read, 'DURATION_AADHAAR_MINOR')
    ]),
    wbta_bp_header: (read) => joinSentences([
      read('consent.title'),
      read('consent.boardingPassSubtitle'),
    ]),
    wbta_hs_bp_header: (read) => joinSentences([
      read('consent.title'),
    ]),
    wbta_bp_body: (read) => joinSentences([
      joinSentences([read('consent.dataHeader'), read('DIGIYATRA_FOUNDATION_AIRPORT')]),
      buildBpSharedData(read),
      joinSentences([read('consent.purposeHeader'), read('PURPOSE_BP')]),
      joinSentences([read('consent.durationHeader'), read('DURATION_BP')]),
      joinSentences([read('IMPORTANT'), read('NO_DATA_BP')]),
      read('I_AGREE_AND_CONFIRM_THE_PRIVACY_POLICY2')
    ]),
    wbta_hs_bp_body: (read) => joinSentences([
      joinSentences([read('DATA_CONSUMER'), read('HS_DATA_CONSUMER_VALUE')]),
      buildBpSharedData(read, true),
      joinSentences([read('consent.purposeHeader'), read('PURPOSE_BP')]),
      joinSentences([read('consent.durationHeader'), read('DURATION_BP')]),
      joinSentences([read('IMPORTANT'), read('NO_DATA_BP')]),
      read('I_AGREE_AND_CONFIRM_THE_PRIVACY_POLICY2')
    ]),
  };

  // Expects nested i18n JSON (same shape as src/locales/english.json).
  const buildConsentAudioEntries = (translatedJson, requestedAudioKeys = null) => {
    const selectedKeysSet = new Set(
      Array.isArray(requestedAudioKeys) && requestedAudioKeys.length > 0
        ? requestedAudioKeys
        : CONSENT_AUDIO_OPTIONS.map((item) => item.key)
    );

    const entries = [];
    const failures = [];

    CONSENT_AUDIO_OPTIONS.forEach(({ key }) => {
      if (!selectedKeysSet.has(key)) return;
      const { read, missingKeys } = createConsentTextReader(translatedJson || {});
      const value = CONSENT_TEXT_BUILDERS[key](read);
      if (missingKeys.size > 0 || !value) {
        failures.push(`${key} (missing: ${[...missingKeys].join(', ') || 'text'})`);
      } else {
        entries.push({ key, value });
      }
    });

    if (failures.length > 0) {
      throw new Error(`Unable to build consent text for: ${failures.join('; ')}`);
    }

    return entries;
  };

  const handleGenerateConsentAudioFiles = async () => {
    if (!outputText.trim()) {
      message.warning('Please paste or translate JSON in the output panel first');
      return;
    }

    try {
      setAudioLoading(true);
      const parsedOutput = validateJSON(outputText);
      const payloadEntries = buildConsentAudioEntries(parsedOutput, selectedAudioKeys);

      const values = {
        data: payloadEntries,
        languageCode: language,
        gender: 'female',
        outputFormat: 'mp3'
      };

      const res = await axiosPost(API_FETCH_AUDIO_FILES, values);
      const files = res?.data?.files || [];
      const skipped = res?.data?.skipped || [];
      const failed = res?.data?.failed || [];

      if (!files.length && !failed.length) {
        message.warning('No consent audio files were generated by backend');
        return;
      }

      if (failed.length) {
        message.warning(`${failed.length} entry/entries failed TTS: ${failed.map(f => f.key).join(', ')}`);
      }
      if (skipped.length) {
        message.info(`${skipped.length} entry/entries skipped (empty value): ${skipped.join(', ')}`);
      }

      const langInfo = ALL_AUDIO_LANGUAGES.find(l => l.code === language);
      const langSuffix = langInfo?.name?.toLowerCase() || language;

      files.forEach((file) => {
        const format = (file.audioFormat || 'mp3').toLowerCase();
        const fileName = `${file.key}_${langSuffix}.${format}`;
        const mimeType = format === 'wav' ? 'audio/wav' : 'audio/mpeg';
        downloadAudioFile(file.audioContent, fileName, mimeType);
      });

      // Export the JSON used for audio generation
      const jsonFileName = `${langInfo?.name?.toLowerCase() || language}.json`;
      await queueBlobDownload(new Blob([outputText], { type: 'application/json' }), jsonFileName);

      message.success(`Generated and downloaded ${files.length} consent audio files + ${jsonFileName}`);
    } catch (audioError) {
      console.error('Consent audio generation error:', audioError);
      const errorMessage =
        audioError?.response?.data?.message ||
        audioError?.message ||
        'Consent audio generation failed';
      message.error(errorMessage);
    } finally {
      setAudioLoading(false);
    }
  };

  const handleAutoGenerateAudioAllLanguages = async () => {
    if (!inputText.trim()) {
      message.warning('Please enter JSON text to translate');
      return;
    }

    if (!selectedAudioKeys.length) {
      message.warning('Please select at least one consent audio to generate');
      return;
    }

    if (!selectedLanguages.length) {
      message.warning('Please select at least one language');
      return;
    }

    setAudioLoading(true);
    setIsProcessing(true);
    setError(null);
    setLanguageErrors([]);
    setAudioProgressLog([]);
    setProgress({ current: 0, total: 0 });

    try {
      const parsedInput = validateJSON(inputText);
      const flatInput = flattenObject(parsedInput);
      const entries = Object.entries(flatInput);
      if (entries.length === 0) {
        throw new Error('JSON object is empty');
      }

      const excludedKeysList = parseExcludedKeys();
      const languagesToProcess = ALL_AUDIO_LANGUAGES.filter(l => selectedLanguages.includes(l.code));
      const totalSteps = languagesToProcess.length;
      setProgress({ current: 0, total: totalSteps });
      const PARALLEL_LIMIT = 3;
      let completedCount = 0;
      const errorsList = [];

      const processLanguageAudio = async (currentLang) => {
        let translatedJSON;

        if (currentLang.code === 'en') {
          // English doesn't need translation - use input directly
          translatedJSON = parsedInput;
        } else {
          const translatableEntries = entries.filter(([key, value]) =>
            typeof value === 'string' && value.trim() !== '' && !excludedKeysList.includes(key)
          );

          const preprocessedData = translatableEntries.map(([key, value]) => {
            const { content, dots } = extractTrailingDots(value);
            const { modifiedText, placeholders } = extractPlaceholders(content);
            return {
              key,
              originalValue: value,
              dotsInfo: { content, dots },
              placeholdersInfo: { modifiedText, placeholders },
              textForTranslation: modifiedText
            };
          });

          const textsForTranslation = preprocessedData.map(item => item.textForTranslation);
          const chunks = createChunks(textsForTranslation, chunkSize);

          let allTranslations = [];
          for (let i = 0; i < chunks.length; i++) {
            const chunkTranslations = await fetchChunkWithRetry(chunks[i], currentLang.code, currentLang.serviceId, i);
            allTranslations = [...allTranslations, ...chunkTranslations];
            if (i < chunks.length - 1) await delay(500);
          }

          translatedJSON = {};
          let translationIndex = 0;
          entries.forEach(([key, value]) => {
            if (excludedKeysList.includes(key)) {
              translatedJSON[key] = value;
            } else if (typeof value === 'string' && value.trim() !== '') {
              let translatedText = allTranslations[translationIndex];
              const item = preprocessedData[translationIndex];
              translatedText = restorePlaceholders(translatedText, item.placeholdersInfo.placeholders);
              if (item.dotsInfo.dots) translatedText += item.dotsInfo.dots;
              translatedJSON[key] = translatedText;
              translationIndex++;
            } else {
              translatedJSON[key] = value;
            }
          });
        }

        // Generate selected audio entries
        let filteredEntries;
        if (skipConsentValidation) {
          // Bypass consent key validation - send all translated values as TTS entries
          const allEntries = Object.entries(translatedJSON)
            .filter(([, value]) => typeof value === 'string' && value.trim())
            .map(([key, value]) => ({ key, value: value.replace(/<\/?[^>]+(>|$)/g, ' ').replace(/\s+/g, ' ').trim() }));
          filteredEntries = selectedAudioKeys.length === CONSENT_AUDIO_OPTIONS.length
            ? allEntries
            : allEntries; // When skipping validation, generate for all keys in JSON
        } else {
          const nestedJSON = currentLang.code === 'en' ? translatedJSON : unflattenObject(translatedJSON);
          filteredEntries = buildConsentAudioEntries(nestedJSON, selectedAudioKeys);
        }

        if (filteredEntries.length > 0) {
          const values = {
            data: filteredEntries,
            languageCode: currentLang.code,
            gender: 'female',
            outputFormat: 'mp3'
          };

          const res = await axiosPost(API_FETCH_AUDIO_FILES, values);
          const files = res?.data?.files || [];
          const failed = res?.data?.failed || [];

          if (failed.length) {
            console.warn(`[${currentLang.name}] TTS failed for: ${failed.map(f => f.key).join(', ')}`);
          }

          if (files.length === 0 && failed.length > 0) {
            // All entries failed TTS - throw to mark this language as failed
            throw new Error(`TTS failed for all entries: ${failed.map(f => `${f.key} (${f.error})`).join('; ')}`);
          }

          files.forEach((file) => {
            const format = (file.audioFormat || 'mp3').toLowerCase();
            const fileName = `${file.key}_${currentLang.name.toLowerCase()}.${format}`;
            const mimeType = format === 'wav' ? 'audio/wav' : 'audio/mpeg';
            downloadAudioFile(file.audioContent, fileName, mimeType);
          });

          // Export the translated JSON for this language
          const jsonFileName = `${currentLang.name.toLowerCase()}.json`;
          await queueBlobDownload(
            new Blob([JSON.stringify(unflattenObject(translatedJSON), null, 2)], { type: 'application/json' }),
            jsonFileName
          );

          const statusMsg = failed.length
            ? `✅ ${currentLang.name}: ${files.length} audio(s) exported, ${failed.length} entry(s) failed TTS`
            : `✅ ${currentLang.name}: ${files.length} audio file(s) + JSON exported`;
          message.success(statusMsg);
        } else {
          message.warning(`⚠️ ${currentLang.name}: No audio entries to process`);
        }

        completedCount++;
        setProgress({ current: completedCount, total: totalSteps });
        setOutputText(`[${completedCount}/${totalSteps}] Completed ${currentLang.name}`);
        setAudioProgressLog(prev => [...prev, { lang: currentLang.name, status: 'success' }]);
      };

      // Process languages in parallel batches of 5
      for (let i = 0; i < languagesToProcess.length; i += PARALLEL_LIMIT) {
        const batch = languagesToProcess.slice(i, i + PARALLEL_LIMIT);
        const results = await Promise.allSettled(batch.map(lang => processLanguageAudio(lang)));
        results.forEach((result, idx) => {
          if (result.status === 'rejected') {
            const errMsg = result.reason?.message || 'Unknown error';
            errorsList.push({ language: batch[idx].name, code: batch[idx].code, error: errMsg });
            setAudioProgressLog(prev => [...prev, { lang: batch[idx].name, status: 'failed', error: errMsg }]);
          }
        });
      }

      setProgress({ current: totalSteps, total: totalSteps });
      setOutputText('');
      setLanguageErrors(errorsList);

      if (errorsList.length > 0) {
        message.warning(`Audio generation completed with ${errorsList.length} error(s). See details below.`);
      } else {
        message.success(`🎉 Audio generation complete for all ${languagesToProcess.length} languages!`);
      }

    } catch (error) {
      console.error("Auto audio generation error:", error);
      setError(error.message || "Audio generation automation failed");
      message.error(error.message || "Audio generation automation failed");
    } finally {
      setAudioLoading(false);
      setIsProcessing(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const fetchBhashiniTranslation = async (textsToTranslate, targetLang, bhashiniServiceId) => {
    try {
      const payload = {
        texts: textsToTranslate,
        targetLanguage: targetLang,
        sourceLanguage: "en",
        serviceId: bhashiniServiceId || undefined,
      };

      const res = await axiosPost(API_TRANSLATE, payload);
      const translations = res?.data?.translations;

      if (!translations || !Array.isArray(translations)) {
        throw new Error('Invalid response structure from translation API');
      }

      return translations;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Translation request failed';
      console.error("Translation API error:", errorMessage);
      throw new Error(errorMessage);
    }
  };

  const fetchChunkWithRetry = async (chunk, targetLang, serviceId, chunkIndex, maxRetries = 3) => {
    let lastError;
    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      try {
        return await fetchBhashiniTranslation(chunk, targetLang, serviceId);
      } catch (err) {
        lastError = err;
        if (attempt <= maxRetries) {
          console.warn(`Chunk ${chunkIndex + 1} failed (attempt ${attempt}/${maxRetries + 1}), retrying...`);
          await delay(1000 * attempt);
        }
      }
    }
    throw new Error(`Chunk ${chunkIndex + 1} failed after ${maxRetries + 1} attempts: ${lastError?.message}`);
  };

  const validateJSON = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      if (typeof parsed !== 'object' || parsed === null) {
        throw new Error('Input must be a valid JSON object or array');
      }
      return parsed;
    } catch (error) {
      throw new Error('Invalid JSON format. Please check your input.');
    }
  };

  const parseExcludedKeys = () => {
    if (!excludedKeys.trim()) return [];
    
    return excludedKeys
      .split(',')
      .map(key => key.trim())
      .filter(key => key.length > 0);
  };

  const handleTranslate = async () => {
    if (!inputText.trim()) {
      message.warning('Please enter JSON text to translate');
      return;
    }

    setLoading(true);
    setIsProcessing(true);
    setError(null);
    setOutputText("");
    setTranslationStats(null);
    setProgress({ current: 0, total: 0 });

    try {
      const startTime = Date.now();

      const parsedInput = validateJSON(inputText);
      const flatInput = flattenObject(parsedInput);
      const entries = Object.entries(flatInput);
      
      if (entries.length === 0) {
        throw new Error('JSON object is empty');
      }

      const excludedKeysList = parseExcludedKeys();

      const translatableEntries = entries.filter(([key, value]) => 
        typeof value === 'string' && 
        value.trim() !== '' && 
        !excludedKeysList.includes(key)
      );

      const excludedEntries = entries.filter(([key, value]) =>
        typeof value === 'string' && 
        value.trim() !== '' && 
        excludedKeysList.includes(key)
      );

      if (translatableEntries.length === 0) {
        throw new Error('No translatable text found in JSON (all keys may be excluded)');
      }

      const preprocessedData = translatableEntries.map(([key, value]) => {
        const { content, dots } = extractTrailingDots(value);
        const { modifiedText, placeholders } = extractPlaceholders(content);
        
        return {
          key,
          originalValue: value,
          dotsInfo: { content, dots },
          placeholdersInfo: { modifiedText, placeholders },
          textForTranslation: modifiedText
        };
      });

      const textsForTranslation = preprocessedData.map(item => item.textForTranslation);
      
      const chunks = createChunks(textsForTranslation, chunkSize);
      setProgress({ current: 0, total: chunks.length });

      let allTranslations = [];
      const lang = SUPPORTED_LANGUAGES.find(item => item.code === language);

      for (let i = 0; i < chunks.length; i++) {
        try {
          const chunkTranslations = await fetchChunkWithRetry(chunks[i], language, lang.serviceId, i);
          allTranslations = [...allTranslations, ...chunkTranslations];
          
          setProgress({ current: i + 1, total: chunks.length });

          const partialJSON = {};
          
          for (let j = 0; j < allTranslations.length; j++) {
            const item = preprocessedData[j];
            let translatedText = allTranslations[j];
            
            translatedText = restorePlaceholders(translatedText, item.placeholdersInfo.placeholders);
            
            if (item.dotsInfo.dots) {
              translatedText = translatedText + item.dotsInfo.dots;
            }
            
            partialJSON[item.key] = translatedText;
          }

          setOutputText(JSON.stringify(unflattenObject(partialJSON), null, 2));
          
          if (i < chunks.length - 1) {
            await delay(1000);
          }
        } catch (chunkError) {
          console.error(`Chunk ${i + 1} exhausted retries:`, chunkError);
          throw new Error(`Translation failed at chunk ${i + 1}: ${chunkError.message}`);
        }
      }

      const finalTranslations = preprocessedData.map((item, index) => {
        let translatedText = allTranslations[index] || item.textForTranslation;
        
        translatedText = restorePlaceholders(translatedText, item.placeholdersInfo.placeholders);
        
        if (item.dotsInfo.dots) {
          translatedText = translatedText + item.dotsInfo.dots;
        }
        
        return {
          key: item.key,
          translatedValue: translatedText
        };
      });

      const finalJSON = {};
      let translationIndex = 0;

      entries.forEach(([key, value]) => {
        if (excludedKeysList.includes(key)) {
          finalJSON[key] = value;
        } else if (typeof value === 'string' && value.trim() !== '') {
          finalJSON[key] = finalTranslations[translationIndex].translatedValue;
          translationIndex++;
        } else {
          finalJSON[key] = value;
        }
      });

      const endTime = Date.now();
      const translationTime = endTime - startTime;

      setOutputText(JSON.stringify(unflattenObject(finalJSON), null, 2));
      setTranslationStats({
        totalKeys: entries.length,
        translatedKeys: translatableEntries.length,
        excludedKeys: excludedEntries.length,
        translationTime: translationTime,
        targetLanguage: language,
        sourceLanguage: 'en',
        chunksProcessed: chunks.length,
        chunkSize: chunkSize,
        placeholdersFound: preprocessedData.reduce((acc, item) => acc + item.placeholdersInfo.placeholders.length, 0),
        dotsPreserved: preprocessedData.filter(item => item.dotsInfo.dots).length
      });

      saveToHistory(finalJSON);

      message.success(
        `Translation completed! Translated ${translatableEntries.length} fields, excluded ${excludedEntries.length} fields.`
      );

    } catch (error) {
      console.error("Translation Error:", error);
      setError(error.message || "Translation failed. Please try again.");
      message.error(error.message || "Translation failed");
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const handleAutoTranslateAll = async () => {
    if (!inputText.trim()) {
      message.warning('Please enter JSON text to translate');
      return;
    }
  
    setLoading(true);
    setIsProcessing(true);
    setError(null);
    setLanguageErrors([]);
    setOutputText("");
    setTranslationStats(null);
    setProgress({ current: 0, total: 0 });
  
    try {
      const parsedInput = validateJSON(inputText);
      const flatInput = flattenObject(parsedInput);
      const entries = Object.entries(flatInput);
      if (entries.length === 0) {
        throw new Error('JSON object is empty');
      }
  
      const excludedKeysList = parseExcludedKeys();
      const PARALLEL_LIMIT = 5;
      const totalSteps = SUPPORTED_LANGUAGES.length;
      let completedCount = 0;
      const errorsList = [];
  
      const processLanguageTranslation = async (currentLang) => {
        const translatableEntries = entries.filter(([key, value]) =>
          typeof value === 'string' &&
          value.trim() !== '' &&
          !excludedKeysList.includes(key)
        );
  
        const preprocessedData = translatableEntries.map(([key, value]) => {
          const { content, dots } = extractTrailingDots(value);
          const { modifiedText, placeholders } = extractPlaceholders(content);
          return {
            key,
            originalValue: value,
            dotsInfo: { content, dots },
            placeholdersInfo: { modifiedText, placeholders },
            textForTranslation: modifiedText
          };
        });
  
        const textsForTranslation = preprocessedData.map(item => item.textForTranslation);
        const chunks = createChunks(textsForTranslation, chunkSize);
  
        let allTranslations = [];
  
        for (let i = 0; i < chunks.length; i++) {
          const chunkTranslations = await fetchChunkWithRetry(chunks[i], currentLang.code, currentLang.serviceId, i);
          allTranslations = [...allTranslations, ...chunkTranslations];
          if (i < chunks.length - 1) {
            await delay(500);
          }
        }
  
        const finalJSON = {};
        let translationIndex = 0;
        entries.forEach(([key, value]) => {
          if (excludedKeysList.includes(key)) {
            finalJSON[key] = value;
          } else if (typeof value === 'string' && value.trim() !== '') {
            let translatedText = allTranslations[translationIndex];
            const item = preprocessedData[translationIndex];
            translatedText = restorePlaceholders(translatedText, item.placeholdersInfo.placeholders);
            if (item.dotsInfo.dots) translatedText += item.dotsInfo.dots;
            finalJSON[key] = translatedText;
            translationIndex++;
          } else {
            finalJSON[key] = value;
          }
        });
  
        // Export file for current language
        const outputJSON = JSON.stringify(unflattenObject(finalJSON), null, 2);
        const blob = new Blob([outputJSON], { type: 'application/json' });
        const fileName = `${currentLang.name.toLowerCase()}.json`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
  
        completedCount++;
        setProgress({ current: completedCount, total: totalSteps });
        setOutputText(`[${completedCount}/${totalSteps}] Exported ${fileName}`);
        message.success(`✅ Exported ${fileName}`);
      };

      // Process languages in parallel batches of 5
      for (let i = 0; i < SUPPORTED_LANGUAGES.length; i += PARALLEL_LIMIT) {
        const batch = SUPPORTED_LANGUAGES.slice(i, i + PARALLEL_LIMIT);
        const results = await Promise.allSettled(batch.map(lang => processLanguageTranslation(lang)));
        results.forEach((result, idx) => {
          if (result.status === 'rejected') {
            const errMsg = result.reason?.message || 'Unknown error';
            errorsList.push({ language: batch[idx].name, code: batch[idx].code, error: errMsg });
          }
        });
      }
  
      setLanguageErrors(errorsList);
      setOutputText('');

      if (errorsList.length > 0) {
        message.warning(`Translation completed with ${errorsList.length} error(s). See details below.`);
      } else {
        message.success("🎉 All languages translated and exported successfully!");
      }
  
    } catch (error) {
      console.error("Auto translation error:", error);
      setError(error.message || "Automation failed");
      message.error(error.message || "Automation failed");
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const handleCopy = async () => {
    if (!outputText) {
      message.warning('No output to copy');
      return;
    }

    try {
      await navigator.clipboard.writeText(outputText);
      message.success('Output copied to clipboard!');
    } catch (error) {
      console.error('Copy error:', error);
      message.error('Failed to copy to clipboard');
    }
  };

  const handleExport = () => {
    if (!outputText) {
      message.warning('No output to export');
      return;
    }

    if (isProcessing) {
      message.warning('Please wait until all chunks are processed');
      return;
    }

    try {
      const selectedLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === language);
      const fileName = `${selectedLanguage?.name?.toLowerCase() || language}.json`;
      
      const blob = new Blob([outputText], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      message.success(`File exported as ${fileName}`);
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export file');
    }
  };

  const handleClear = () => {
    setInputText("");
    setOutputText("");
    setExcludedKeys("");
    setError(null);
    setTranslationStats(null);
    setLanguageErrors([]);
    setProgress({ current: 0, total: 0 });
    message.success('Form cleared successfully!');
  };

  const clearHistory = () => {
    setTranslationHistory([]);
    message.success('Translation history cleared!');
  };

  const selectedLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === language);

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2} style={{ marginBottom: '8px', fontSize: '32px' }}>
            <GlobalOutlined style={{ marginRight: '12px' }} />
            Bhashini Translation Service
          </Title>
        </div>

        {translationHistory.length > 0 && (
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>Recent Translations</Text>
                <Button 
                  size="small" 
                  danger 
                  icon={<ClearOutlined />} 
                  onClick={clearHistory}
                >
                  Clear History
                </Button>
              </div>
            }
            style={{ marginBottom: '24px' }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {translationHistory.slice(0, 5).map((item) => (
                <Tag 
                  key={item.id}
                  color="blue" 
                  style={{ marginBottom: '4px' }}
                >
                  {SUPPORTED_LANGUAGES.find(l => l.code === item.targetLanguage)?.name} - {item.keyCount} keys
                  <Text type="secondary" style={{ fontSize: '11px', marginLeft: '4px' }}>
                    ({new Date(item.timestamp).toLocaleDateString()})
                  </Text>
                </Tag>
              ))}
            </div>
          </Card>
        )}

        {(loading || audioLoading) && progress.total > 0 && (
          <Card style={{ marginBottom: '24px' }}>
            <div style={{ textAlign: 'center' }}>
              <Text strong>Processing {audioLoading ? 'Audio Generation' : 'Translation'}</Text>
              <Progress 
                percent={Math.round((progress.current / progress.total) * 100)}
                format={() => `${progress.current}/${progress.total}`}
                style={{ marginTop: '8px' }}
                status={isProcessing ? "active" : "success"}
              />
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary">
                  {isProcessing ? 'Translation in progress... Partial results shown below' : 'Translation complete!'}
                </Text>
              </div>
            </div>
          </Card>
        )}

        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '12px',
          marginBottom: '24px',
          padding: '12px 16px',
          background: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
        }}>
          <Select
            value={language}
            onChange={setLanguage}
            size="large"
            style={{ minWidth: 220 }}
            disabled={loading}
          >
            {ALL_AUDIO_LANGUAGES.map((lang) => (
              <Option key={lang.code} value={lang.code}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{lang.name}</span>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {lang.nativeName}
                  </Text>
                </div>
              </Option>
            ))}
          </Select>

          <Select
            value={chunkSize}
            onChange={setChunkSize}
            size="large"
            style={{ minWidth: 150 }}
            disabled={loading}
            placeholder="Chunk Size"
          >
            {CHUNK_SIZE_OPTIONS.map((size) => (
              <Option key={size} value={size}>
                Chunk Size: {size}
              </Option>
            ))}
          </Select>

          <Button
            type="primary"
            size="large"
            icon={<TranslationOutlined />}
            onClick={handleTranslate}
            loading={loading}
            disabled={!inputText.trim() || language === 'en'}
          >
            {loading ? "Translating..." : "Translate"}
          </Button>
          <Button
            type="default"
            size="large"
            icon={<GlobalOutlined />}
            onClick={handleAutoTranslateAll}
            loading={loading}
            disabled={!inputText.trim() || language === 'en'}
          >
            {loading ? "Processing..." : "Auto Translate All"}
          </Button>
          <Button
            size="large"
            icon={<SoundOutlined />}
            onClick={handleGenerateConsentAudioFiles}
            loading={audioLoading}
            disabled={!outputText.trim() || loading}
          >
            {audioLoading ? 'Generating Consent Audio...' : 'Generate Consent Audios'}
          </Button>
          <Button 
            size="large"
            icon={<ClearOutlined />}
            onClick={handleClear}
            disabled={loading || audioLoading}
          >
            Clear
          </Button>
        </div>

        {/* Auto Audio Generation Section */}
        <Collapse
          style={{ marginBottom: '24px', background: '#fff' }}
          items={[{
            key: 'auto-audio',
            label: 'Auto Generate Consent Audios',
            children: (
              <>
                <div style={{ marginBottom: 12 }}>
                  <Checkbox
                    checked={skipConsentValidation}
                    onChange={(e) => setSkipConsentValidation(e.target.checked)}
                  >
                    Skip consent key validation (use all input keys as direct TTS entries)
                  </Checkbox>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Select consent audios to generate"
                    value={selectedAudioKeys}
                    onChange={setSelectedAudioKeys}
                    options={CONSENT_AUDIO_OPTIONS.map(o => ({ label: o.label, value: o.key }))}
                    disabled={skipConsentValidation}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Select languages"
                    value={selectedLanguages}
                    onChange={setSelectedLanguages}
                    options={ALL_AUDIO_LANGUAGES.map(l => ({ label: `${l.name} (${l.nativeName})`, value: l.code }))}
                    maxTagCount="responsive"
                  />
                </div>
                <Button
                  type="primary"
                  icon={<SoundOutlined />}
                  onClick={handleAutoGenerateAudioAllLanguages}
                  loading={audioLoading}
                  disabled={!inputText.trim() || !selectedLanguages.length || loading || (!skipConsentValidation && !selectedAudioKeys.length)}
                >
                  {audioLoading ? `Generating (${progress.current}/${progress.total})...` : `Generate Selected Audios for ${selectedLanguages.length} Language(s)`}
                </Button>
                {audioProgressLog.length > 0 && (
                  <div style={{ marginTop: 16, maxHeight: 200, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>Progress Log:</Text>
                    {audioProgressLog.map((log, idx) => (
                      <div key={idx} style={{ marginBottom: 4 }}>
                        {log.status === 'success'
                          ? <Tag color="success">{log.lang}</Tag>
                          : <><Tag color="error">{log.lang}</Tag><Text type="secondary" style={{ fontSize: 11 }}>{log.error}</Text></>
                        }
                      </div>
                    ))}
                  </div>
                )}
              </>
            )
          }]}
        />

        {(translationStats || error || languageErrors.length > 0) && (
          <div style={{ marginBottom: '24px' }}>
            {languageErrors.length > 0 && (
              <Alert
                message={`${languageErrors.length} Language(s) Failed`}
                description={
                  <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                    {languageErrors.map((item, idx) => (
                      <div key={idx} style={{ marginBottom: '4px' }}>
                        <Tag color="error">{item.language} ({item.code})</Tag>
                        <Text type="secondary" style={{ fontSize: '12px' }}>{item.error}</Text>
                      </div>
                    ))}
                  </div>
                }
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined />}
                closable
                onClose={() => setLanguageErrors([])}
                style={{ marginBottom: '16px' }}
              />
            )}
            {translationStats && (
              <Alert
                message="Translation Complete"
                description={
                  <div>
                    <Text>
                      Translated {translationStats.translatedKeys} of {translationStats.totalKeys} fields 
                      in {translationStats.translationTime}ms
                    </Text>
                    {translationStats.excludedKeys > 0 && (
                      <>
                        <br />
                        <Text type="secondary" style={{ color: '#fa8c16' }}>
                          🚫 Excluded {translationStats.excludedKeys} keys (kept in English)
                        </Text>
                      </>
                    )}
                    <br />
                    <Text type="secondary">
                      Language: {selectedLanguage?.name} ({selectedLanguage?.nativeName})
                    </Text>
                    <br />
                    <Text type="secondary">
                      Processed in {translationStats.chunksProcessed} chunks (size: {translationStats.chunkSize})
                    </Text>
                    {translationStats.placeholdersFound > 0 && (
                      <>
                        <br />
                        <Text type="secondary" style={{ color: '#52c41a' }}>
                          🔒 Preserved {translationStats.placeholdersFound} placeholders
                        </Text>
                      </>
                    )}
                    {translationStats.dotsPreserved > 0 && (
                      <>
                        <br />
                        <Text type="secondary" style={{ color: '#52c41a' }}>
                          📍 Preserved trailing dots in {translationStats.dotsPreserved} strings
                        </Text>
                      </>
                    )}
                  </div>
                }
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                closable
                onClose={() => setTranslationStats(null)}
                style={{ marginBottom: error ? '16px' : '0' }}
              />
            )}

            {error && (
              <Alert
                message="Translation Error"
                description={error}
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined />}
                closable
                onClose={() => setError(null)}
              />
            )}
          </div>
        )}

        <Card 
          title={
            <Space>
              <ClearOutlined />
              <Text strong>Excluded Keys (Optional)</Text>
            </Space>
          }
          style={{ marginBottom: '24px' }}
        >
          <TextArea
            placeholder="Enter keys to exclude from translation, separated by commas. Example: TERMS_URL, PRIVACY_URL, API_KEY"
            value={excludedKeys}
            onChange={(e) => setExcludedKeys(e.target.value)}
            rows={3}
            disabled={loading}
            style={{ 
              fontFamily: 'Monaco, Consolas, monospace',
              resize: 'none'
            }}
          />
          <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
            These keys will remain in English in the output JSON
          </Text>
        </Card>

        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Card 
              title={
                <Space>
                  <FileTextOutlined />
                  <Text strong>Input (English JSON)
                    <Text style={{color: 'red'}}>*</Text>
                  </Text>
                </Space>
              }
              style={{ height: '600px' }}
            >
              <TextArea
                placeholder='Enter JSON here, e.g., { "HELLO": "Hello...", "WELCOME": "Welcome {{name}} to our service..." }'
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                rows={20}
                disabled={loading}
                style={{ 
                  fontFamily: 'Monaco, Consolas, monospace',
                  height: '100%',
                  resize: 'none'
                }}
              />
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <GlobalOutlined />
                    <Text strong>
                      Output ({selectedLanguage?.name})
                    </Text>
                    {isProcessing && (
                      <Tag color="processing">Processing...</Tag>
                    )}
                  </Space>
                  {outputText && (
                    <Space>
                      <Button 
                        icon={<CopyOutlined />} 
                        onClick={handleCopy}
                        disabled={isProcessing}
                      >
                        Copy
                      </Button>
                      <Button 
                        icon={<DownloadOutlined />} 
                        onClick={handleExport}
                        type="primary"
                        disabled={isProcessing}
                        title={isProcessing ? "Please wait until all chunks are processed" : "Export JSON"}
                      >
                        Export JSON
                      </Button>
                    </Space>
                  )}
                </div>
              }
              style={{ height: '600px' }}
            >
              <div style={{ height: '100%' }}>
                <Spin spinning={loading && !outputText}>
                  <TextArea
                    placeholder="Paste or translate JSON here to generate consent audio files..."
                    value={outputText}
                    onChange={(e) => setOutputText(e.target.value)}
                    rows={20}
                    style={{ 
                      height: '100%',
                      fontFamily: 'Monaco, Consolas, monospace',
                      resize: 'none'
                    }}
                  />
                </Spin>
              </div>
            </Card>
          </Col>
        </Row>

        <Card style={{ marginTop: '24px' }} title="Enhanced Features">
          <Row gutter={16}>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px' }}>
                <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>🔒 Placeholder Preservation</Text>
                <br />
                <Text type="secondary">
                  Keeps placeholders like {'{{'}{'{'}name{'}'}{'}'} intact
                </Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px' }}>
                <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>📍 Dot Handling</Text>
                <br />
                <Text type="secondary">
                  Preserves trailing dots (...) in text
                </Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px' }}>
                <Text strong style={{ color: '#fa8c16', fontSize: '16px' }}>🚫 Key Exclusion</Text>
                <br />
                <Text type="secondary">
                  Exclude keys from translation
                </Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px' }}>
                <Text strong style={{ color: '#722ed1', fontSize: '16px' }}>⚡ Batch Processing</Text>
                <br />
                <Text type="secondary">
                  Configurable chunk sizes
                </Text>
              </div>
            </Col>
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default BhashiniService;